/**
 * src/shell/auth/tailscale-admin-guard.ts
 *
 * Enforces Tailscale mesh perimeter checks for all admin/root authentication
 * and privileged control plane mutations.
 */

/**
 * Checks whether an IP address belongs to the Tailscale CGNAT subnet (100.64.0.0/10)
 * or local loopback (127.0.0.1 / ::1 / localhost).
 */
export function isTailscaleOrLocal(ip: string): boolean {
  if (!ip) return false;

  const cleanIp = ip.trim().toLowerCase();

  // Localhost & Loopback
  if (
    cleanIp === '127.0.0.1' ||
    cleanIp === '::1' ||
    cleanIp === 'localhost' ||
    cleanIp === '::ffff:127.0.0.1'
  ) {
    return true;
  }

  // Handle IPv4-mapped IPv6 (e.g. ::ffff:100.66.28.42)
  const ipv4 = cleanIp.startsWith('::ffff:') ? cleanIp.slice(7) : cleanIp;

  // Validate IPv4 format
  const parts = ipv4.split('.').map((p) => parseInt(p, 10));
  if (parts.length !== 4 || parts.some((p) => isNaN(p) || p < 0 || p > 255)) {
    return false;
  }

  // 100.64.0.0/10 check: First octet must be 100, second octet between 64 and 127
  const [o1, o2] = parts;
  if (o1 === 100 && o2 >= 64 && o2 <= 127) {
    return true;
  }

  return false;
}

export interface AdminAccessVerification {
  allowed: boolean;
  clientIp: string;
  reason?: string;
  instructionsUrl?: string;
}

export class TailscaleAdminGuard {
  private auditLog: Array<{ ip: string; timestamp: number; allowed: boolean; userAgent?: string }> = [];

  /**
   * Validates if client IP has access to privileged admin endpoints.
   */
  public verifyAdminAccess(clientIp: string, userAgent?: string): AdminAccessVerification {
    const isAllowed = isTailscaleOrLocal(clientIp);

    this.auditLog.push({
      ip: clientIp,
      timestamp: Date.now(),
      allowed: isAllowed,
      userAgent,
    });

    // Keep log bounded
    if (this.auditLog.length > 500) {
      this.auditLog.shift();
    }

    if (!isAllowed) {
      return {
        allowed: false,
        clientIp,
        reason: 'TAILSCALE_NETWORK_REQUIRED: Admin privileges and SuperAdmin dashboard access are restricted to nodes authenticated on the Sovereign Tailscale mesh (100.64.0.0/10).',
        instructionsUrl: 'https://mesh.herx.org/docs/tailnet-join',
      };
    }

    return {
      allowed: true,
      clientIp,
    };
  }

  /**
   * Retrieves recent audit logs for security review.
   */
  public getAuditLogs() {
    return [...this.auditLog];
  }
}

export const globalTailscaleAdminGuard = new TailscaleAdminGuard();
